import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Plus,
  Edit2,
  Trash2,
  Copy,
  X,
  Code,
  Search,
  Loader2,
  GitBranch,
  FileCode2,
  Check
} from "lucide-react";
import api from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";

const BusinessFlows = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [flows, setFlows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalLoading, setIsModalLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Create / Edit Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    content: "",
    keywords: [],
    is_active: true
  });
  const [keywordInput, setKeywordInput] = useState("");

  // Code / Logic Viewer Modal state
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [selectedFlowDetail, setSelectedFlowDetail] = useState(null);
  const [isCodeLoading, setIsCodeLoading] = useState(false);
  const [codeModalTab, setCodeModalTab] = useState("logic"); // "logic" | "json"
  const [isCopied, setIsCopied] = useState(false);

  // Delete confirmation state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [flowToDelete, setFlowToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch flows from API
  const fetchFlows = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await api.getFlows();
      setFlows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Error fetching business flows:", error);
      showToast("Error fetching business flows", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchFlows();
  }, [activeAiAgentId, fetchFlows]);

  // Open Create / Edit modal
  const handleOpenModal = async (flow = null) => {
    if (flow) {
      setEditingFlow(flow);
      setIsModalLoading(true);
      setIsModalOpen(true);
      try {
        // Fetch complete detail with content
        const detail = await api.getFlowDetail(flow.id);
        setFormData({
          name: detail.name || flow.name || "",
          description: detail.description || flow.description || "",
          content: detail.content || "",
          keywords: Array.isArray(detail.keywords) ? detail.keywords : (flow.keywords || []),
          is_active: detail.is_active !== undefined ? detail.is_active : (flow.is_active !== undefined ? flow.is_active : true)
        });
      } catch (err) {
        console.error("Failed to load flow details:", err);
        setFormData({
          name: flow.name || "",
          description: flow.description || "",
          content: flow.content || "",
          keywords: flow.keywords || [],
          is_active: flow.is_active !== undefined ? flow.is_active : true
        });
      } finally {
        setIsModalLoading(false);
      }
    } else {
      setEditingFlow(null);
      setFormData({
        name: "",
        description: "",
        content: "",
        keywords: [],
        is_active: true
      });
      setIsModalOpen(true);
    }
    setKeywordInput("");
  };

  // Open View Logic / Code Modal
  const handleOpenCodeModal = async (flow) => {
    setIsCodeModalOpen(true);
    setIsCodeLoading(true);
    setSelectedFlowDetail(flow);
    setCodeModalTab("logic");
    setIsCopied(false);

    try {
      const detail = await api.getFlowDetail(flow.id);
      setSelectedFlowDetail(detail);
    } catch (error) {
      console.error("Error fetching flow detail:", error);
      showToast("Failed to fetch full flow details", "error");
    } finally {
      setIsCodeLoading(false);
    }
  };

  // Open Delete modal
  const handleOpenDeleteModal = (flow) => {
    setFlowToDelete(flow);
    setIsDeleteModalOpen(true);
  };

  // Handle Form Submit (Create / Update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      showToast("Flow name is required", "error");
      return;
    }
    if (!formData.content.trim()) {
      showToast("Flow logic definition is required", "error");
      return;
    }

    try {
      setIsModalLoading(true);
      if (editingFlow) {
        await api.updateFlow(editingFlow.id, {
          name: formData.name.trim(),
          description: formData.description.trim(),
          content: formData.content,
          keywords: formData.keywords || [],
          is_active: formData.is_active
        });
        showToast("Business flow updated successfully");
      } else {
        await api.saveFlow({
          name: formData.name.trim(),
          description: formData.description.trim(),
          content: formData.content,
          keywords: formData.keywords || [],
          is_active: formData.is_active,
          agent_id: activeAiAgentId
        });
        showToast("Business flow created successfully");
      }
      setIsModalOpen(false);
      fetchFlows();
    } catch (error) {
      console.error("Error saving business flow:", error);
      const detail = error?.response?.detail || error?.message || "Failed to save business flow";
      showToast(detail, "error");
    } finally {
      setIsModalLoading(false);
    }
  };

  // Delete Flow
  const handleDelete = async () => {
    if (!flowToDelete) return;
    setIsDeleting(true);
    try {
      await api.deleteFlow(flowToDelete.id);
      showToast("Business flow deleted successfully");
      setIsDeleteModalOpen(false);
      setFlowToDelete(null);
      fetchFlows();
    } catch (error) {
      console.error("Error deleting flow:", error);
      showToast("Failed to delete business flow", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  // Toggle Flow Active State
  const toggleFlowActive = async (flow) => {
    try {
      let content = flow.content;
      if (!content) {
        const detail = await api.getFlowDetail(flow.id);
        content = detail?.content || "Default workflow logic";
      }

      await api.updateFlow(flow.id, {
        name: flow.name,
        description: flow.description || "",
        content: content,
        keywords: flow.keywords || [],
        is_active: !flow.is_active
      });

      showToast(`Business flow "${flow.name}" ${!flow.is_active ? "activated" : "deactivated"}`);
      fetchFlows();
    } catch (error) {
      console.error("Error toggling flow active state:", error);
      showToast("Failed to update flow status", "error");
    }
  };

  // Add keyword chip
  const handleAddKeyword = () => {
    const trimmed = keywordInput.trim();
    if (!trimmed) return;
    if (!(formData.keywords || []).includes(trimmed)) {
      setFormData({
        ...formData,
        keywords: [...(formData.keywords || []), trimmed]
      });
    }
    setKeywordInput("");
  };

  // Remove keyword chip
  const handleRemoveKeyword = (indexToRemove) => {
    setFormData({
      ...formData,
      keywords: (formData.keywords || []).filter((_, idx) => idx !== indexToRemove)
    });
  };

  // Copy helper
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setIsCopied(true);
    showToast(`${label} copied to clipboard`);
    setTimeout(() => setIsCopied(false), 2000);
  };

  // Filtered flows
  const filteredFlows = useMemo(() => {
    if (!flows) return [];
    const q = searchQuery.trim().toLowerCase();
    if (!q) return flows;
    return flows.filter(
      (f) =>
        f.name?.toLowerCase().includes(q) ||
        String(f.id)?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        (f.keywords && f.keywords.some((k) => k.toLowerCase().includes(q)))
    );
  }, [flows, searchQuery]);

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Business Flows</h1>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Define and orchestrate multi-step organizational logic, triggers, and workflow sequences.
            </p>
          </div>
          <button
            onClick={() => handleOpenModal()}
            className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs active:scale-[0.98] self-start sm:self-auto cursor-pointer"
          >
            <Plus size={14} strokeWidth={2.5} />
            <span>Create Flow</span>
          </button>
        </div>

        {/* Search Bar */}
        <div className="pt-5 pb-4">
          <div className="relative w-full sm:max-w-md">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-zinc-400">
              <Search size={14} strokeWidth={2.5} />
            </span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by flow name, ID, description, or keyword..."
              className="w-full text-xs border border-zinc-200 rounded-lg pl-9 pr-3 py-2 focus:border-zinc-900 focus:outline-none bg-white transition-colors text-zinc-800 font-medium placeholder-zinc-400 shadow-2xs"
            />
          </div>
        </div>

        {/* Main Content Card */}
        <div className="bg-white rounded-xl border border-zinc-200 shadow-2xs overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 size={24} className="animate-spin text-zinc-400" />
              <p className="text-xs font-semibold text-zinc-500">Loading business flows...</p>
            </div>
          ) : filteredFlows.length === 0 ? (
            <div className="p-12 text-center">
              <h3 className="text-sm font-semibold text-zinc-900 mb-1">
                {searchQuery ? "No matching flows found" : "No Business Flows Yet"}
              </h3>
              <p className="text-xs text-zinc-500 max-w-sm mx-auto mb-4">
                {searchQuery
                  ? `No flows match "${searchQuery}". Clear your search query.`
                  : "Create structured procedural flows to instruct your AI agents how to resolve specific customer requests."}
              </p>
              {!searchQuery && (
                <button
                  onClick={() => handleOpenModal()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-all font-semibold text-xs shadow-xs cursor-pointer"
                >
                  <Plus size={13} strokeWidth={2.5} />
                  <span>Create First Flow</span>
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile Card List (visible on screens < md) */}
              <div className="block md:hidden divide-y divide-zinc-100">
                {filteredFlows.map((flow) => (
                  <div key={flow.id} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <h4 className="font-bold text-zinc-900 text-sm">{flow.name}</h4>
                        <button
                          onClick={() => handleOpenModal(flow)}
                          className="text-zinc-400 hover:text-zinc-900 p-1 rounded hover:bg-zinc-100"
                          title="Edit Flow"
                        >
                          <Edit2 size={12} />
                        </button>
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleFlowActive(flow)}
                        title={flow.is_active ? "Deactivate Flow" : "Activate Flow"}
                        className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer ${
                          flow.is_active ? "bg-zinc-900" : "bg-zinc-200"
                        }`}
                      >
                        <div
                          className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform shadow-xs ${
                            flow.is_active ? "left-4" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>

                    {flow.description && (
                      <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                        {flow.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-[11px] bg-zinc-50 p-2 rounded-lg border border-zinc-100">
                      <span className="text-zinc-400 font-medium">Flow ID:</span>
                      <div className="flex items-center gap-1.5 font-mono text-zinc-700">
                        <span>#{flow.id}</span>
                        <button
                          onClick={() => handleCopy(String(flow.id), "Flow ID")}
                          className="p-1 text-zinc-400 hover:text-zinc-900"
                        >
                          <Copy size={11} />
                        </button>
                      </div>
                    </div>

                    {flow.keywords && flow.keywords.length > 0 && (
                      <div className="flex flex-wrap gap-1 pt-0.5">
                        {flow.keywords.map((kw, i) => (
                          <span
                            key={i}
                            className="bg-zinc-100/80 text-zinc-700 text-[10px] font-medium px-2 py-0.5 rounded border border-zinc-200/60"
                          >
                            {kw}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-100">
                      <button
                        onClick={() => handleOpenCodeModal(flow)}
                        className="px-2.5 py-1.5 text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-lg text-xs font-semibold flex items-center gap-1"
                      >
                        <Code size={13} />
                        <span>Logic</span>
                      </button>
                      <button
                        onClick={() => handleOpenDeleteModal(flow)}
                        className="px-2.5 py-1.5 text-rose-600 bg-rose-50 hover:bg-rose-100 rounded-lg text-xs font-semibold flex items-center gap-1"
                      >
                        <Trash2 size={13} />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table (visible on screens >= md) */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-zinc-200 bg-zinc-50/50">
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Flow Name</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Flow ID</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Description</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider">Keywords / Triggers</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-center">Status</th>
                      <th className="px-4 py-3 text-[11px] font-semibold text-zinc-500 uppercase tracking-wider text-right pr-6">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100 text-xs">
                    {filteredFlows.map((flow) => (
                      <tr key={flow.id} className="hover:bg-zinc-50/70 transition-colors group">
                        {/* Name */}
                        <td className="px-4 py-3.5 font-semibold text-zinc-900 align-middle">
                          <div className="flex items-center gap-2">
                            <span className="truncate max-w-[180px]">{flow.name}</span>
                            <button
                              onClick={() => handleOpenModal(flow)}
                              className="text-zinc-400 hover:text-zinc-900 transition-colors p-1 rounded hover:bg-zinc-100 cursor-pointer"
                              title="Edit Flow"
                            >
                              <Edit2 size={12} />
                            </button>
                          </div>
                        </td>

                        {/* ID */}
                        <td className="px-4 py-3.5 align-middle">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[11px] text-zinc-600 bg-zinc-100/80 px-2 py-0.5 rounded border border-zinc-200/60">
                              #{flow.id}
                            </span>
                            <button
                              onClick={() => handleCopy(String(flow.id), "Flow ID")}
                              className="p-1 text-zinc-400 hover:text-zinc-900 transition-colors rounded hover:bg-zinc-100 cursor-pointer"
                              title="Copy Flow ID"
                            >
                              <Copy size={12} />
                            </button>
                          </div>
                        </td>

                        {/* Description */}
                        <td className="px-4 py-3.5 text-zinc-500 max-w-[220px] truncate align-middle">
                          {flow.description || "—"}
                        </td>

                        {/* Keywords */}
                        <td className="px-4 py-3.5 align-middle max-w-[240px]">
                          {flow.keywords && flow.keywords.length > 0 ? (
                            <div className="flex flex-wrap gap-1 items-center">
                              {flow.keywords.slice(0, 3).map((kw, i) => (
                                <span
                                  key={i}
                                  className="bg-zinc-100/80 text-zinc-700 text-[10px] font-medium px-2 py-0.5 rounded border border-zinc-200/60"
                                >
                                  {kw}
                                </span>
                              ))}
                              {flow.keywords.length > 3 && (
                                <span className="text-[10px] font-semibold text-zinc-400">
                                  +{flow.keywords.length - 3}
                                </span>
                              )}
                            </div>
                          ) : (
                            <span className="text-zinc-300 text-[11px]">—</span>
                          )}
                        </td>

                        {/* Status Toggle */}
                        <td className="px-4 py-3.5 align-middle text-center">
                          <div className="flex items-center justify-center">
                            <button
                              type="button"
                              onClick={() => toggleFlowActive(flow)}
                              title={flow.is_active ? "Deactivate Flow" : "Activate Flow"}
                              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer focus:outline-none ${
                                flow.is_active ? "bg-zinc-900" : "bg-zinc-200 hover:bg-zinc-300"
                              }`}
                            >
                              <div
                                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform shadow-xs ${
                                  flow.is_active ? "left-4" : "left-0.5"
                                }`}
                              />
                            </button>
                          </div>
                        </td>

                        {/* Actions */}
                        <td className="px-4 py-3.5 align-middle text-right pr-6">
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              onClick={() => handleOpenCodeModal(flow)}
                              className="p-1.5 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors border border-transparent hover:border-zinc-200 cursor-pointer"
                              title="View Flow Logic"
                            >
                              <Code size={14} />
                            </button>
                            <button
                              onClick={() => handleOpenDeleteModal(flow)}
                              className="p-1.5 text-zinc-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                              title="Delete Flow"
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Create / Edit Flow Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-xl border border-zinc-200">
            <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center">
              <div>
                <h3 className="text-sm font-bold text-zinc-900">
                  {editingFlow ? "Edit Business Flow" : "Create Business Flow"}
                </h3>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Define sequence title, keywords, and instructional execution steps
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-1 text-zinc-400 hover:text-zinc-900 rounded-lg transition-colors cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {/* Name */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700">Flow Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 text-xs transition-colors"
                  placeholder="e.g. order_cancellation_workflow"
                />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-[11px] font-semibold text-zinc-700">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 text-xs transition-colors resize-none"
                  placeholder="Brief note on when the AI agent should execute this workflow..."
                  rows={2}
                />
              </div>

              {/* Keywords / Trigger Shortcuts */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-semibold text-zinc-700">Keywords & Triggers</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddKeyword();
                      }
                    }}
                    className="flex-1 px-3 py-2 bg-white border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 text-xs transition-colors"
                    placeholder="Type trigger keyword and press Enter..."
                  />
                  <button
                    type="button"
                    onClick={handleAddKeyword}
                    className="px-3 py-2 bg-zinc-100 hover:bg-zinc-200 text-zinc-800 rounded-lg font-semibold text-xs transition-colors cursor-pointer"
                  >
                    Add
                  </button>
                </div>
                {formData.keywords && formData.keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {formData.keywords.map((kw, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-800 text-[11px] font-medium px-2.5 py-1 rounded-md border border-zinc-200"
                      >
                        {kw}
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyword(index)}
                          className="text-zinc-400 hover:text-rose-500 font-bold ml-0.5 leading-none cursor-pointer"
                        >
                          &times;
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Logic Definition */}
              <div className="space-y-1">
                <div className="flex items-center justify-between">
                  <label className="text-[11px] font-semibold text-zinc-700">Logic Definition (Steps / Prompt) *</label>
                  <span className="text-[10px] text-zinc-400">
                    {formData.content?.length || 0} / 5000 chars
                  </span>
                </div>
                <textarea
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  className="w-full px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-lg focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono text-xs transition-colors resize-y min-h-[140px]"
                  placeholder={`Step 1: Ask user for order ID\nStep 2: Check order status via API tool\nStep 3: If status is 'Processing', proceed with cancellation\nStep 4: Confirm cancellation with customer`}
                  rows={6}
                />
              </div>

              {/* Is Active Toggle */}
              <div className="flex items-center justify-between pt-1">
                <div>
                  <span className="text-xs font-semibold text-zinc-900">Active State</span>
                  <p className="text-[11px] text-zinc-500">Allow agent to trigger this sequence</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                  className={`w-9 h-5 rounded-full transition-colors relative flex-shrink-0 cursor-pointer ${
                    formData.is_active ? "bg-zinc-900" : "bg-zinc-200"
                  }`}
                >
                  <div
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-transform shadow-xs ${
                      formData.is_active ? "left-4.5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-2 pt-3 border-t border-zinc-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-zinc-600 hover:text-zinc-900 text-xs font-semibold rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isModalLoading}
                  className="px-4 py-2 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-all active:scale-[0.98] disabled:opacity-50 cursor-pointer"
                >
                  {isModalLoading && <Loader2 size={13} className="animate-spin" />}
                  <span>{editingFlow ? "Save Changes" : "Create Flow"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Code & Logic Viewer Modal */}
      {isCodeModalOpen && (
        <div className="fixed inset-0 bg-zinc-900/40 backdrop-blur-sm flex items-center justify-center z-[200] p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden shadow-xl border border-zinc-200 flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b border-zinc-100 flex justify-between items-center bg-zinc-50/50">
              <div className="flex items-center gap-3">
                <FileCode2 className="text-zinc-700 shrink-0" size={18} />
                <div>
                  <h3 className="text-sm font-bold text-zinc-900">
                    {selectedFlowDetail?.name || "Flow Logic"}
                  </h3>
                  <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-0.5">
                    <span className="font-mono">ID: #{selectedFlowDetail?.id}</span>
                    <span>•</span>
                    <span className={selectedFlowDetail?.is_active ? "text-emerald-600 font-semibold" : "text-zinc-400"}>
                      {selectedFlowDetail?.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const text = codeModalTab === "logic"
                      ? (selectedFlowDetail?.content || "")
                      : JSON.stringify(selectedFlowDetail, null, 2);
                    handleCopy(text, codeModalTab === "logic" ? "Logic script" : "JSON schema");
                  }}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-zinc-700 bg-white hover:bg-zinc-100 rounded-lg border border-zinc-200 transition-colors shadow-2xs cursor-pointer"
                >
                  {isCopied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                  <span>{isCopied ? "Copied" : "Copy"}</span>
                </button>
                <button
                  onClick={() => setIsCodeModalOpen(false)}
                  className="p-1.5 text-zinc-400 hover:text-zinc-900 rounded-lg transition-colors hover:bg-zinc-100 cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="px-6 pt-3 pb-2 flex items-center gap-2 border-b border-zinc-100 bg-white">
              <button
                type="button"
                onClick={() => setCodeModalTab("logic")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  codeModalTab === "logic"
                    ? "bg-zinc-900 text-white shadow-xs"
                    : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                }`}
              >
                Instruction Logic
              </button>
              <button
                type="button"
                onClick={() => setCodeModalTab("json")}
                className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                  codeModalTab === "json"
                    ? "bg-zinc-900 text-white shadow-xs"
                    : "text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                }`}
              >
                Full JSON Schema
              </button>
            </div>

            {/* Code Content */}
            <div className="p-6 flex-1 overflow-y-auto bg-zinc-950 text-zinc-100 font-mono text-xs leading-relaxed">
              {isCodeLoading ? (
                <div className="flex items-center justify-center py-12 gap-2 text-zinc-400">
                  <Loader2 size={18} className="animate-spin" />
                  <span>Loading flow definition...</span>
                </div>
              ) : codeModalTab === "logic" ? (
                <pre className="whitespace-pre-wrap font-mono">
                  {selectedFlowDetail?.content || "// No logic instructions provided."}
                </pre>
              ) : (
                <pre className="whitespace-pre-wrap font-mono text-emerald-400">
                  {JSON.stringify(selectedFlowDetail, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Business Flow"
        message={`Are you sure you want to delete flow "${flowToDelete?.name}"? This action cannot be reversed.`}
        loading={isDeleting}
      />
    </div>
  );
};

export default BusinessFlows;
